"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  Check,
  Search,
  FolderOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  getCollections,
  searchCollections,
  type Collection,
} from "@/services/collectionService";

interface CollectionSelectorProps {
  label?: string;
  value?: string[];
  onValueChange: (collectionIds: string[]) => void;
  placeholder?: string;
  error?: string;
  helperText?: string;
  maxSelections?: number;
  multiSelect?: boolean;
  seedCollections?: Collection[];
}

interface CollectionOption {
  id: string;
  title: string;
  status?: string;
  image?: string;
  productCount: number;
}

const collectionToOption = (collection: Collection): CollectionOption => ({
  id: collection.id || collection.slug || collection.title || "collection",
  title: collection.title || collection.slug || collection.id || "Collection",
  status: collection.status,
  image: collection.image,
  productCount: collection.products_count ?? collection.total_products ?? 0,
});

const mergeCollectionOptions = (
  existing: CollectionOption[],
  incoming: CollectionOption[],
): CollectionOption[] => {
  const byId = new Map<string, CollectionOption>();
  for (const item of existing) byId.set(item.id, item);
  for (const item of incoming) byId.set(item.id, item);
  return Array.from(byId.values());
};

const loadingPlaceholder = (id: string): CollectionOption => ({
  id,
  title: "Loading…",
  status: "DRAFT",
  image: undefined,
  productCount: 0,
});

const extractCollections = (response: any): Collection[] => {
  const payload = response?.data ?? response;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload)) return payload;
  return [];
};

export const CollectionSelector: React.FC<CollectionSelectorProps> = ({
  label = "Select Collections",
  value = [],
  onValueChange,
  placeholder = "Search and select collections",
  error,
  helperText,
  maxSelections = 10,
  multiSelect = true,
  seedCollections,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [collections, setCollections] = useState<CollectionOption[]>([]);
  const [loading, setLoading] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const didLoadInitialCatalogRef = useRef(false);
  const resolveAttemptedRef = useRef<Set<string>>(new Set());

  const displaySelected = useMemo(() => {
    return value.map((id) => {
      const collection = collections.find((item) => item.id === id);
      return collection ?? loadingPlaceholder(id);
    });
  }, [value, collections]);

  const loadInitialCollections = async () => {
    try {
      setLoading(true);
      const response = await getCollections({
        page: 1,
        pageSize: 10,
        sortBy: "created_at",
        sortOrder: "desc",
      });
      const collectionOptions = extractCollections(response).map(
        collectionToOption,
      );
      setCollections((prev) => mergeCollectionOptions(prev, collectionOptions));
    } catch (error) {
      console.error("[CollectionSelector] Failed to load initial collections", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!seedCollections?.length) return;
    const incoming = seedCollections.map(collectionToOption);
    setCollections((prev) => mergeCollectionOptions(prev, incoming));
  }, [seedCollections]);

  useEffect(() => {
    const missing = value.filter((id) => !collections.some((item) => item.id === id));
    const toResolve = missing.filter((id) => !resolveAttemptedRef.current.has(id));
    if (!toResolve.length) return;

    toResolve.forEach((id) => resolveAttemptedRef.current.add(id));
    let cancelled = false;

    void (async () => {
      const response = await getCollections({
        page: 1,
        pageSize: Math.max(toResolve.length, 10),
      });
      if (cancelled) return;

      const resolved = extractCollections(response).reduce<CollectionOption[]>(
        (acc, collection) => {
          if (collection.id && toResolve.includes(collection.id)) {
            acc.push(collectionToOption(collection));
          }
          return acc;
        },
        [],
      );

      const fallback = toResolve
        .filter((id) => !resolved.some((item) => item.id === id))
        .map((id) => ({
          id,
          title: "Unavailable",
          status: "DRAFT",
          image: undefined,
          productCount: 0,
        }));

      setCollections((prev) =>
        mergeCollectionOptions(prev, [...resolved, ...fallback]),
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [value, collections]);

  useEffect(() => {
    if (!open || didLoadInitialCatalogRef.current) return;
    didLoadInitialCatalogRef.current = true;
    void loadInitialCollections();
  }, [open]);

  const searchCollectionsApi = async (query: string) => {
    if (!query.trim()) {
      void loadInitialCollections();
      return;
    }

    try {
      setLoading(true);
      const response = await searchCollections({
        query,
        page: 1,
        pageSize: 20,
      });
      const collectionOptions = extractCollections(response).map(
        collectionToOption,
      );
      setCollections((prev) => mergeCollectionOptions(prev, collectionOptions));
    } catch (error) {
      console.error("[CollectionSelector] Failed to search collections", error);
      const filtered = collections.filter((collection) =>
        collection.title.toLowerCase().includes(query.toLowerCase()),
      );
      setCollections(filtered);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      void searchCollectionsApi(query);
    }, 300);
  };

  const handleCollectionSelect = (collection: CollectionOption) => {
    if (!multiSelect) {
      onValueChange([collection.id]);
      setOpen(false);
      setSearchQuery("");
      return;
    }

    const isSelected = value.includes(collection.id);
    let nextSelection: string[];

    if (isSelected) {
      nextSelection = value.filter((id) => id !== collection.id);
    } else {
      if (value.length >= maxSelections) return;
      nextSelection = [...value, collection.id];
    }

    onValueChange(nextSelection);
  };

  const handleRemoveCollection = (
    collectionId: string,
    e: React.MouseEvent | React.KeyboardEvent,
  ) => {
    e.stopPropagation();
    onValueChange(value.filter((id) => id !== collectionId));
  };

  return (
    <div className="space-y-1">
      <label className="block text-[13px] font-normal text-muted-foreground">
        {label}
      </label>
      <div className="bg-background border border-border rounded-xl overflow-hidden">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              role="combobox"
              aria-expanded={open}
              className="w-full h-auto min-h-[38px] justify-between bg-transparent hover:bg-transparent px-4 py-2 font-normal"
            >
              <div className="flex-1 text-left">
                {displaySelected.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {displaySelected.map((collection) => (
                      <span
                        key={collection.id}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-xs"
                      >
                        {collection.title}
                        <button
                          type="button"
                          onClick={(e) => handleRemoveCollection(collection.id, e)}
                          className="hover:text-destructive ml-1"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted-foreground">{placeholder}</span>
                )}
              </div>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <div className="flex items-center border-b px-3 py-2 gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search collections..."
                className="w-full bg-transparent outline-none text-sm"
              />
            </div>
            <div className="max-h-72 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-sm text-muted-foreground">
                  Loading collections...
                </div>
              ) : collections.length > 0 ? (
                collections.map((collection) => {
                  const selected = value.includes(collection.id);
                  return (
                    <button
                      key={collection.id}
                      type="button"
                      onClick={() => handleCollectionSelect(collection)}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted transition-colors",
                        selected && "bg-primary/5",
                      )}
                    >
                      <span className="flex h-8 w-8 items-center justify-center rounded-md bg-muted text-xs text-muted-foreground">
                        <FolderOpen className="h-4 w-4" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block truncate text-sm font-medium">
                          {collection.title}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {collection.status || "DRAFT"} · {collection.productCount} products
                        </span>
                      </span>
                      {selected && <Check className="h-4 w-4 text-primary" />}
                    </button>
                  );
                })
              ) : (
                <div className="p-4 text-sm text-muted-foreground">
                  No collections found.
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {helperText && !error && (
        <p className="text-xs text-muted-foreground">{helperText}</p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
};
